import { useState, useEffect } from 'react';
import { Search, useTranslation, FAIcon, InputValidations, Theme, MLPVersion } from '@nextgen-web-framework/all';
import { Logger, nextgenDebounce } from '@nextgen-web-framework/core';
import type { DynamicObjectInterface } from '@commerce-profile-webcomponents/sdk';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, AppState } from '@nextgen-web-framework/core';
import { profileApi, updateRegistrationData } from '@commerce-profile-webcomponents/sdk';
import { useGlobalErrorHandler } from '../../utils/globalErrorHook';
import { OnSelectSearchEnum } from '../../utils/types/enum';
import { AddressLookupProps, AddressLookupItem } from './types/propTypes';
import styles from './AddressLookup.module.scss';

const AddressLookup = ({
  title,
  contactType,
  className,
  callback,
  allowEnterManually = true,
  status,
}: AddressLookupProps) => {
  const t = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const logger = Logger.getInstance();

  const registrationFlowData = useSelector((state: AppState) => state.commerceProfile.registrationFlowData);
  const { flowName, perosonalDetails } = registrationFlowData;
  const { isFieldDisabled } = perosonalDetails;
  const addressesLabel = t('profile.AddressInput.addresses');

  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [searchResult, setSearchResult] = useState<AddressLookupItem[]>([]);
  const [showNoResult, setShowNoResult] = useState<boolean>(false);
  const apiErrorHandler = useGlobalErrorHandler();

  const addressTypeLabel = title ?? t(`profile.AddressInput.${contactType.toLowerCase()}Address`);

  const addressNoResult: AddressLookupItem = {
    matchId: '',
    type: OnSelectSearchEnum.NO_RESULT,
    text: t('profile.AddressInput.unableToLocateAddress'),
    display: t('profile.AddressInput.unableToLocateAddress'),
    count: 0,
  };

  const addressManual: AddressLookupItem = {
    matchId: '',
    type: OnSelectSearchEnum.MANUAL,
    text: t('profile.AddressInput.enterAddressManually'),
    display: t('profile.AddressInput.enterAddressManually'),
    count: 0,
  };
  const onSearchFocus = (isFocused: boolean) => {
    dispatch(
      updateRegistrationData({
        perosonalDetails: {
          ...perosonalDetails,
          isAddressSearchFocused: isFocused,
        },
      }),
    );
  };

  useEffect(() => {
    triggerInputChange();
  }, [searchKeyword]);

  const handleSearch = async () => {
    if (searchKeyword.length >= 1) {
      let results: AddressLookupItem[] = [];

      try {
        const response = await apiErrorHandler(() => profileApi.contactSearch(searchKeyword));
        results = response.searchResults as AddressLookupItem[];
        results.sort((a: DynamicObjectInterface, b: DynamicObjectInterface) => b.type?.localeCompare(a.type));
        results.forEach((item: AddressLookupItem) => {
          if (item.type === 'Container') {
            item.display = `${item.text} (${item.count} ${addressesLabel})`;
          } else {
            item.display = item.text;
          }
        });
      } catch (err) {
        logger.error('Address not found', err);
        // No other action
      }

      if (results.length === 0) {
        results.push(addressNoResult);
        setShowNoResult(true);
      } else {
        setShowNoResult(false);
      }

      if (allowEnterManually) {
        results.push(addressManual);
      }

      setSearchResult(results);
    }
  };

  const handleSearchContainer = async (containerId: string) => {
    const response = await apiErrorHandler(() => profileApi.contactSearchContainer(containerId));
    const results: AddressLookupItem[] = response.searchResults as AddressLookupItem[];
    results.forEach((item: AddressLookupItem) => {
      item.display = <>{item.text}</>;
    });

    setShowNoResult(false);
    if (results.length === 0) {
      results.push(addressNoResult);
      setShowNoResult(true);
    }

    if (allowEnterManually) {
      results.push(addressManual);
    }
    setSearchResult(results);
  };

  const handleGetContact = async (matchId: string) => {
    const response = await apiErrorHandler(() => profileApi.contactSearchMatchId(matchId.replace('/', '|')));
    if (response && callback) {
      callback({
        address: {
          ...response,
          contactType: contactType,
        },
      });
    }
  };

  const searchOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchKeyword(e.target.value);
    onSearchFocus(true);
  };

  const triggerInputChange = nextgenDebounce(handleSearch, 500);

  const searchOnClear = () => {
    setSearchKeyword('');
    onSearchFocus(false);
  };

  const onSearchSelect = (item: AddressLookupItem) => {
    switch (item.type) {
      case OnSelectSearchEnum.CONTAINER:
        item.matchId && handleSearchContainer(item.matchId);
        break;
      case OnSelectSearchEnum.ADDRESS:
        item.matchId && handleGetContact(item.matchId);
        break;
      case OnSelectSearchEnum.MANUAL:
        callback?.({
          manualEntry: true,
        });
        break;
      default:
    }
  };

  return (
    <div className={`${styles['address-lookup-container']} ${className}`} data-testid="address-lookup-container-testid">
      <div>
        <Search
          onChange={searchOnChange}
          value={searchKeyword}
          onSearch={() => null}
          mlpVersion={MLPVersion.TWO}
          placeholder={`${t('profile.AddressInput.searchFor')} ${addressTypeLabel}`}
          data={searchResult as DynamicObjectInterface}
          className={`${styles['search-input']} ${showNoResult ? styles['show-no-result'] : ''} ${
            allowEnterManually ? styles['show-manual'] : ''
          } `}
          onClear={searchOnClear}
          theme={status === InputValidations.INVALID ? Theme.ERROR : Theme.BLACK}
          disabled={flowName ? isFieldDisabled : false}
        />
        {searchResult.length > 0 && (
          <div className={styles['search-results']}>
            {searchResult?.map((item: DynamicObjectInterface) => (
              <div
                onClick={() => onSearchSelect(item)}
                tabIndex={-1}
                aria-hidden="true"
                data-testId="search-result-testid"
                className={`${styles['search-item']} ${styles[item.type]}`}
                key={item.id}
              >
                <div className={styles['display']}>{item.display}</div>
                <div className={styles['chevron']}>
                  {item.type === 'Container' && <FAIcon iconName="chevron-right" />}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
export default AddressLookup;
